import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, ChevronRight, ChevronLeft, X, Check, Users, Mail, Eye, Tag } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { createPortal } from "react-dom";

const TEAL = "#0D9488";

/** 60-second onboarding wizard shown ONCE to a newly verified coach.
 *  Steps: 1) bio + specialties  2) directory opt-in  3) digest opt-in.
 *  Skipping or finishing hits POST /coach/onboarded so it never re-fires.
 */
export default function CoachOnboardingWizard({ open, initial, allowed, onClose }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    coach_bio: initial?.coach_bio || "",
    coach_specialties: initial?.coach_specialties || [],
    directory_enabled: initial?.directory_enabled ?? true,
    coach_digest_enabled: initial?.coach_digest_enabled ?? true,
    accepting_clients: initial?.accepting_clients ?? true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") finish(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const toggleSpecialty = (s) => {
    setForm((f) => {
      const has = f.coach_specialties.includes(s);
      if (has) return { ...f, coach_specialties: f.coach_specialties.filter((x) => x !== s) };
      if (f.coach_specialties.length >= 5) return f;
      return { ...f, coach_specialties: [...f.coach_specialties, s] };
    });
  };

  const finish = async (skipped = false) => {
    setBusy(true);
    try {
      // Persist whatever they've picked (still respect skip — save what's there)
      await api.patch("/coach/profile", {
        coach_bio: form.coach_bio,
        coach_specialties: form.coach_specialties,
        directory_enabled: !!form.directory_enabled,
        coach_digest_enabled: !!form.coach_digest_enabled,
        accepting_clients: !!form.accepting_clients,
      }).catch(() => {});
      await api.post("/coach/onboarded");
      if (!skipped) toast.success("Coach profile ready 🎉");
      onClose?.(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (step < 2) setStep((s) => s + 1);
    else finish(false);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const steps = [
    {
      key: "bio",
      title: "Tell users who you are",
      body: "This is what appears on the Find-a-Coach directory next to your name.",
      render: () => (
        <div className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">
              Your public bio
            </label>
            <Textarea
              autoFocus
              value={form.coach_bio}
              onChange={(e) => setForm((f) => ({ ...f, coach_bio: e.target.value.slice(0, 600) }))}
              placeholder="e.g. Certified performance coach — 6 yrs helping athletes dial in peptide + macro protocols."
              rows={4}
              className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
              data-testid="wizard-bio-input"
            />
            <div className="text-[10px] text-slate-400 font-mono-num mt-1">{form.coach_bio.length}/600</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 flex items-center gap-1.5">
              <Tag className="w-3 h-3" /> Your specialties (pick up to 5)
            </label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="wizard-specialty-chips">
              {(allowed || []).map((s) => {
                const active = form.coach_specialties.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSpecialty(s)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-mono-num uppercase tracking-wider border transition-colors ${
                      active
                        ? "bg-[#0D9488] text-white border-[#0D9488]"
                        : "bg-white text-slate-600 border-slate-200 hover:border-teal-300 hover:text-teal-700"
                    }`}
                    data-testid={`wizard-specialty-${s}`}
                  >
                    {s.replace("-", " ")}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "directory",
      title: "Get discovered",
      body: "Turn on the Find-a-Coach directory so users looking for a coach can request access to their logs with one tap.",
      render: () => (
        <div className="space-y-4">
          <WizardToggle
            icon={Eye}
            label="Show me in the Find-a-Coach directory"
            hint="Verified coaches with this on appear on every user's dashboard."
            checked={form.directory_enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, directory_enabled: v }))}
            testid="wizard-toggle-directory"
          />
          <WizardToggle
            icon={Users}
            label="I'm accepting new clients"
            hint="Turn off later if you're at capacity — your profile still shows, but the request button is disabled."
            checked={form.accepting_clients}
            onCheckedChange={(v) => setForm((f) => ({ ...f, accepting_clients: v }))}
            testid="wizard-toggle-accepting"
          />
        </div>
      ),
    },
    {
      key: "digest",
      title: "Stay in the loop",
      body: "Get one email every Monday morning summarising your clients' past 7 days — shots, meals, weigh-ins, delta weight.",
      render: () => (
        <div className="space-y-4">
          <WizardToggle
            icon={Mail}
            label="Email me a weekly client digest"
            hint="Every Monday 08:00 UTC. One clean summary covering every active client."
            checked={form.coach_digest_enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, coach_digest_enabled: v }))}
            testid="wizard-toggle-digest"
          />
          <div className="rounded-lg bg-teal-50 border border-teal-100 p-4 text-sm text-teal-800 leading-relaxed">
            You can change any of this anytime from <b>My Clients → Coach settings</b>. Ready to open your Coach hub?
          </div>
        </div>
      ),
    },
  ];

  const active = steps[step];

  return createPortal(
    <div
      className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => finish(true)}
      data-testid="coach-wizard-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="coach-wizard-panel"
      >
        <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#0D9488]" />
            <span className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">
              Coach onboarding · {step + 1} / {steps.length}
            </span>
          </div>
          <button
            type="button"
            onClick={() => finish(true)}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            data-testid="coach-wizard-close"
            aria-label="Close wizard"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="h-1 bg-slate-50 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[#0D9488] transition-all duration-300"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>

        <div className="px-6 py-5">
          <h2 className="font-display font-bold text-2xl text-slate-900 mb-1" data-testid="coach-wizard-title">
            {active.title}
          </h2>
          <p className="text-sm text-slate-500 mb-5 leading-relaxed">{active.body}</p>
          {active.render()}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/60">
          <button
            type="button"
            onClick={() => finish(true)}
            className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-800 transition-colors"
            data-testid="coach-wizard-skip"
          >
            Skip for now
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={back}
                disabled={busy}
                className="text-slate-700 hover:bg-slate-100"
                data-testid="coach-wizard-back"
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              onClick={next}
              disabled={busy}
              className="text-white"
              style={{ background: TEAL }}
              data-testid="coach-wizard-next"
            >
              {step < 2 ? (
                <>Next <ChevronRight className="w-3.5 h-3.5 ml-1" /></>
              ) : (
                <><Check className="w-3.5 h-3.5 mr-1" /> Finish</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function WizardToggle({ icon: Icon, label, hint, checked, onCheckedChange, testid }) {
  return (
    <div className="flex items-start gap-3 border border-slate-200 rounded-lg p-3">
      <Icon className="w-4 h-4 mt-0.5 shrink-0 text-[#0D9488]" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{hint}</div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="shrink-0 data-[state=checked]:bg-[#0D9488]"
        data-testid={testid}
      />
    </div>
  );
}
